import React, {memo, useRef, useEffect, useMemo} from 'react';
import {View} from 'react-native';
import Animated, {useCode, block, set, cond, eq} from 'react-native-reanimated';
import {timing} from 'react-native-redash';
import {createUseStyle} from '@redlink/assets/styles';

/**
 * @typedef {object} PageIndicatorProps
 * @property {number} [count] Default value is **3**
 * @property {number} [selectedIndex] Default value is **0**
 * @property {number} [size] Default value is **5**
 * @property {number} [scaleRate] Default value is **1.5**
 * @property {string} [color]
 * @property {string} [tintColor]
 */

/** @type {React.FC<PageIndicatorProps>} */
export const PageIndicator = memo(
    ({
         count = 3,
         selectedIndex = 0,
         size = 5,
         scaleRate = 1.5,
         color,
         tintColor,
     }) => {
        const indicatorList = useMemo(() => Array(count).fill(0), [count]);
        const lastIndex = useRef(selectedIndex);
        const indicatorListScale = useMemo(
            () => indicatorList.map(() => new Animated.Value(1)),
            [count] // eslint-disable-line react-hooks/exhaustive-deps
        );
        const {styles, colors} = useStyle();
        color = color ?? colors.cardBackground;
        tintColor = tintColor ?? colors.primary;
        useCode(
            () =>
                block(
                    indicatorList.reduce((prev, p, index, array) => {
                        const isSelected = index === selectedIndex;
                        const isLastSelected = index === lastIndex.current;
                        const mustBeChanged = isSelected || isLastSelected;
                        if (mustBeChanged) {
                            return [
                                ...prev,
                                set(
                                    indicatorListScale[index],
                                    timing({
                                        from: isSelected || !isLastSelected ? 1 : scaleRate,
                                        to: isSelected ? scaleRate : 1,
                                    })
                                ),
                            ];
                        } else {
                            return prev;
                        }
                    }, [])
                ),
            [selectedIndex]
        );
        useEffect(() => {
            lastIndex.current = selectedIndex;
        }, [selectedIndex]);

        return (
            <View style={styles.container}>
                {indicatorList.map((p, index) => {
                    // const backgroundColor = interpolateColor(indicatorListScale[index], {
                    //     inputRange: [1, 1.5],
                    //     outputRange: [color, tintColor],
                    // });
                    const backgroundColor = cond(eq(indicatorListScale[index], 1), color, tintColor);
                    return (
                        <Animated.View
                            style={[
                                {
                                    transform: [{scale: indicatorListScale[index]}],
                                    width: size,
                                    height: size,
                                    borderRadius: size / 2,
                                    marginHorizontal: size / 2,
                                    backgroundColor,
                                },
                            ]}
                        />
                    );
                })}
            </View>
        );
    }
);

const useStyle = createUseStyle(({colors, size, ltrFlexDirection}) => ({
    container: {
        flexDirection: ltrFlexDirection,
    },
}));
